import { useState } from 'react';

import differenceInDays from 'date-fns/differenceInDays';
import ReactGA from 'react-ga4';

import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';

import { RootState } from '@share/utils';
import { IHotelInfo, IRoomsContent } from '@common-types';
import { IAccount } from '@share/common-types';
import { EmptySearchMessage } from '@components';
import { roomsSearchActions } from '@store/slices';
import { InfoGraySvg } from '@assets';
import { CloseSvg } from '@share/assets';
import { D_VIEW_ALL_ROOMS } from '@share/constants';

import { RoomsSkeleton } from '../rooms-skeleton';
import { RoomsSearchListItem } from '../rooms-search-list-item';

import './style.scss';

interface IProps {
  hotel: IHotelInfo;
  isVacationRentals?: boolean;
  isFocused?: boolean;
  isStaticDetail?: boolean;
}

const zero = 0;

export function RoomsSearchList(props: IProps) {
  const [showMoreRooms, setShowMoreRooms] = useState(false);

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const hotelsStore = useSelector((state: RootState) => state.hotelsStore);
  const hotelDetailsStore = useSelector((state: RootState) => state.hotelDetailsStore);
  const roomsSearchStore = useSelector((state: RootState) => state.roomsSearchStore);
  
  const { hotel, isVacationRentals, isStaticDetail } = props;

  const { account } = loginStore;
  const { hotel: hotelDetails, loadingRoom } = hotelDetailsStore;

  const dispatch = useDispatch();

  const getHotelInfo = (): IHotelInfo => {
    return roomsSearchStore.hotel ? roomsSearchStore.hotel : hotel;
  };

  const handleViewMOreRooms = () => {
    setShowMoreRooms(!showMoreRooms);

    ReactGA.event({
      category: account?.name as string,
      action: `${D_VIEW_ALL_ROOMS}_${account?.name.toUpperCase()}`,
      label: `User clicked view more/less rooms on detail`,
      nonInteraction: false,
    });
  }

  const getRoomContentBase = (content: any, room: string): IRoomsContent => {
    return content
      ? content
      : {
          amenities: [],
          descriptions: [],
          images: [],
          roomKey: room,
        };
  };

  const getRoomContentAshevillePlay = (room: string): IRoomsContent => {
    const { roomsContent = [] } = hotelDetails;
    const content = roomsContent.find(({ roomId }: any) => roomId === room);
    return getRoomContentBase(content, room);
  };

  const getRoomContent = (room: string): IRoomsContent => {
    const { roomsContent = [] } = getHotelInfo();
    const content = roomsContent.find(({ roomKey }) => roomKey === room);
    return getRoomContentBase(content, room);
  };

  const { endDate, startDate, isRoomsLoading, showUpdatedBanner } = roomsSearchStore;
  const { selectedHotel } = hotelsStore;
  const { adjustedPackageGroups } = getHotelInfo();

  const nights = differenceInDays(new Date(endDate || ''), new Date(startDate || ''));

  const groupPackages = adjustedPackageGroups?.map(({ groupedPackages }) => get(groupedPackages, `[${zero}]`, null)).filter(p => !!p);

  const cheapestRoom = adjustedPackageGroups?.length ? groupPackages?.reduce((prev: any, current: any) => (+current?.pricePerNight >= +prev?.pricePerNight)? prev :  current) : null;
  const preferredRoomPackageId = get(cheapestRoom, `packageId`, null);
  const preferredRoomSavingsAmount = get(cheapestRoom, `savings`, 0);
  const preferredRoomPrice = get(cheapestRoom, `price`, 1);
  const preferredRoomSavingPct = preferredRoomSavingsAmount / (preferredRoomPrice + preferredRoomSavingsAmount) * 100;

  const isVacationRental = hotel.hotelDetails?.isVacationRental;

  const list = adjustedPackageGroups?.length ? 
                    adjustedPackageGroups : 
                    isStaticDetail ? 
                        hotelDetails?.roomsContent : 
                        adjustedPackageGroups;

  return (
    <div className="rooms-search__list">
      {isRoomsLoading || loadingRoom ? (
        <div className="rooms-search__list-skeleton">
          <RoomsSkeleton />
          <RoomsSkeleton />
          <RoomsSkeleton />
        </div>
      ) : (
        <>
          {!!showUpdatedBanner && !!adjustedPackageGroups?.length ? (
            <div className="rooms-search__updated-banner">
              <div className="rooms-search__banner-icon">
                <InfoGraySvg />
              </div>
              <div className="rooms-search__updated-text-wrapper">
                <div className="rooms-search__updated-header">
                  <FormattedMessage id="rooms.updated" />
                </div>
                <div className="rooms-search__updated-text">
                  <FormattedMessage id="last.session.expired" />
                </div>
              </div>
              <div
                className="rooms-search__updated-close"
                onClick={() => dispatch(roomsSearchActions.setShowUpdatedBanner(false))}
              >
                <CloseSvg />
              </div>
            </div>) : null}

          {!!list?.length && (
            <div className={`rooms-search__list-text secondary-font ${isVacationRental ? 'vacation' : ''}`}>
              {
                isVacationRental
                ? <div className="rooms-search__header secondary-font" style={{ fontSize: '24px' }}>
                    {isVacationRental ? (<FormattedMessage id="units" />) : (<FormattedMessage id="rooms" />)}
                  </div>
                : <span className="list-count"><FormattedMessage id="room.search.room_availability" /> {list.length}</span>
              }
              {list?.length > 6 && !showMoreRooms && (<span className="view-all" onClick={handleViewMOreRooms}><FormattedMessage id="room.search.view_all" /></span>)}
              {list?.length > 6 && showMoreRooms && (<span className="view-all" onClick={handleViewMOreRooms}><FormattedMessage id="room.search.view_less" /></span>)}
            </div>)}

          <div className={`rooms-search__list-wrapper ${(isStaticDetail && !adjustedPackageGroups?.length) ? 'static-detail-list' : ''}`}>
            {adjustedPackageGroups?.length ? (
              adjustedPackageGroups.slice(0, (!showMoreRooms? 6 : adjustedPackageGroups.length)).map(({ groupedPackages }) => {
                return (
                  <RoomsSearchListItem
                    hotel={hotel}
                    isVacationRental={isVacationRental}
                    nights={nights}
                    groupedPackages={groupedPackages}
                    isStaticDetail={isStaticDetail}

                    getRoomContent={getRoomContent}
                    preferredRoomSavingPct={preferredRoomSavingPct}
                    preferredRoomPackageId={preferredRoomPackageId}
                    selectedHotel={selectedHotel}
                  />);
              })
            ) : (
              (hotelDetails?.roomsContent?.length > 0) ? (
                hotelDetails?.roomsContent.slice(0, (!showMoreRooms? 6 : hotelDetails?.roomsContent.length)).map((r: any, i: number) => {
                  return (
                    <RoomsSearchListItem
                      hotel={hotel}
                      isVacationRental={isVacationRental}
                      nights={nights}
                      groupedPackages={[
                        {
                          hotelId: hotel.hotelDetails?.id!,
                          packageId: i.toString(),
                          rooms: [
                            {
                              adultsCount: r.maxOccupancy,
                              bedType: r.bedType?.[0],
                              id: r.roomId,
                              roomName: r.name,
                              targetRoomKey: r.roomId,
                              descriptions: r.descriptions,
                            }
                          ],
                        }
                      ]}
                      isStaticDetail={isStaticDetail}
  
                      getRoomContent={getRoomContentAshevillePlay}
                      preferredRoomSavingPct={preferredRoomSavingPct}
                      preferredRoomPackageId={preferredRoomPackageId}
                      selectedHotel={selectedHotel}
                    />);
                })
              ) : (
                <div className="rooms-search__list-wrapper-no-rooms">
                  <EmptySearchMessage 
                    account={account as IAccount} 
                    label={"no.available.rooms"} 
                    isVacationRentals={isVacationRentals}
                  />
                </div>)
            )}
          </div>

          {!!list?.length && list?.length > 6 && (
            <div className="rooms-search__list-show-more secondary-font" onClick={handleViewMOreRooms}>
              {!showMoreRooms && (<><span><FormattedMessage id="room.search.view_all_rooms" /></span><FontAwesomeIcon icon={faArrowDown}/></>)}
              {showMoreRooms && (<><span><FormattedMessage id="room.search.view_less_rooms" /></span><FontAwesomeIcon icon={faArrowUp}/></>)}
            </div>)}

        </>
      )}
    </div>
  );
}

